import { z } from 'zod'

/**
 * Contract for AI image editing.
 *
 * Served by `/api/v1/image-jobs`. Generation is asynchronous — a Gemini edit
 * takes tens of seconds — so creating a job returns immediately and the client
 * polls until the status settles.
 *
 * The operations are the agency's own list, split by the kind of photograph
 * they are written for: an interior and an exterior edit of "Personen
 * entfernen" share a name but not a prompt.
 *
 * Intended model: Google's Gemini image editing family ("Nano Banana").
 * `features/image-ai/operations.ts` holds the prompts.
 */
export const imageOperationSchema = z.enum([
  // --- Innenaufnahmen ---
  'interior_light',
  'interior_light_warm',
  'interior_surfaces',
  'interior_people',
  'interior_photos_remove',
  'interior_photos_replace',
  'interior_perspective',
  'interior_declutter',
  // --- Außenansichten ---
  'exterior_declutter',
  'exterior_clean',
  'exterior_light_sky',
  'exterior_vehicles',
  'exterior_housenumber',
  'exterior_plates',
  'exterior_people',
  'exterior_perspective',
])

/** Which kind of photograph an operation is written for. */
export const imageSceneSchema = z.enum(['interior', 'exterior'])

/**
 * `queued` and `running` are separate on purpose: a generative job waits in a
 * provider queue long before it starts, and a UI that shows "läuft" for a job
 * that has not started is lying about how long is left.
 */
export const imageJobStatusSchema = z.enum(['queued', 'running', 'succeeded', 'failed'])

export const imageJobSchema = z.object({
  id: z.string(),
  estateId: z.number().int(),
  /** onOffice file id of the source picture. */
  sourceImageId: z.number().int(),
  /**
   * Base CDN url of the source, resolved BY THE SERVER from the local mirror.
   * The client never supplies it — see `createImageJobSchema`.
   */
  sourceUrl: z.string(),
  operation: imageOperationSchema,
  /** Free-text steering typed by the agent; null for operations that take none. */
  prompt: z.string().nullable(),
  style: z.string().nullable(),
  /**
   * The full instruction that went to the model — constraints, operation and
   * the agent's own words, assembled server-side. Stored so a prompt can be
   * reviewed against its result instead of guessed at.
   */
  modelPrompt: z.string(),
  /** The model that produced it, so a result stays attributable after a switch. */
  model: z.string(),
  status: imageJobStatusSchema,
  /**
   * Deliberately no `progress`: one Gemini call reports none, and a server that
   * invents a percentage is lying. The UI estimates from `startedAt` and the
   * operation's expected duration, and says "ca.".
   */
  resultUrl: z.string().nullable(),
  error: z.string().nullable(),
  createdAt: z.iso.datetime(),
  startedAt: z.iso.datetime().nullable(),
  finishedAt: z.iso.datetime().nullable(),
})

/**
 * What the browser is allowed to ask for.
 *
 * Deliberately NOT the source url and NOT the prompt. The server resolves the
 * url from `estates.images` by id — otherwise the endpoint would fetch whatever
 * address a caller named, which is an SSRF hole — and builds the prompt from
 * the shared catalogue, so `BASE_CONSTRAINTS` cannot be dropped by a caller.
 */
export const createImageJobSchema = z.object({
  estateId: z.number().int(),
  sourceImageId: z.number().int(),
  operation: imageOperationSchema,
  /** Free text from the agent. Subordinated to the constraints when assembled. */
  prompt: z.string().max(2000).nullable().default(null),
  /** Label of one of the operation's style variants. */
  style: z.string().max(120).nullable().default(null),
})

export const imageJobListSchema = z.object({
  jobs: z.array(imageJobSchema),
  /** False when OPENROUTER_API_KEY is unset — the UI says so instead of failing. */
  configured: z.boolean(),
})

export type ImageOperation = z.infer<typeof imageOperationSchema>
export type ImageScene = z.infer<typeof imageSceneSchema>
export type ImageJobStatus = z.infer<typeof imageJobStatusSchema>
export type ImageJob = z.infer<typeof imageJobSchema>
export type CreateImageJob = z.infer<typeof createImageJobSchema>
export type ImageJobList = z.infer<typeof imageJobListSchema>
